export const unlockOutreachChannels = ['Email', 'Phone', 'SMS', 'LinkedIn', 'WhatsApp'] as const
const workingDaysPerWeek = 5
const workingDaysPerMonth = 20
const emailTouchesPerLeadPerMonth = 2
const emailMailboxDailyCapacity = 20
const emailMailboxesPerDomain = 5
const linkedinWeeklyCapacityPerAccount = 200
const smsDailyCapacityPerNumber = 2000
const whatsappDailyCapacityPerNumber = 1000

export function calculateOutreachResources(weeklyLeads: number | string, selectedChannels: readonly string[] = unlockOutreachChannels) {
  const weeklyLeadsValue = Number(weeklyLeads)
  const hasWeeklyLeadsValue = Number.isFinite(weeklyLeadsValue) && weeklyLeadsValue > 0
  const weeklyLeadTarget = hasWeeklyLeadsValue ? Math.ceil(weeklyLeadsValue) : 0
  const dailyLeadTarget = hasWeeklyLeadsValue ? Math.ceil(weeklyLeadTarget / workingDaysPerWeek) : 0
  const emailMonthlyVolume = hasWeeklyLeadsValue ? weeklyLeadTarget * 4 * emailTouchesPerLeadPerMonth : 0
  const emailMailboxMonthlyCapacity = emailMailboxDailyCapacity * workingDaysPerMonth
  const emailMailboxesNeeded = hasWeeklyLeadsValue ? Math.max(1, Math.ceil(emailMonthlyVolume / emailMailboxMonthlyCapacity)) : 0
  const emailDomainsNeeded = emailMailboxesNeeded > 0 ? Math.max(1, Math.ceil(emailMailboxesNeeded / emailMailboxesPerDomain)) : 0
  const linkedinAccountsNeeded = hasWeeklyLeadsValue ? Math.max(1, Math.ceil(weeklyLeadTarget / linkedinWeeklyCapacityPerAccount)) : 0
  const linkedinWeeksSingleAccount = hasWeeklyLeadsValue ? Math.max(1, Math.ceil(weeklyLeadTarget / linkedinWeeklyCapacityPerAccount)) : 0
  const smsNumbersNeeded = hasWeeklyLeadsValue ? Math.max(1, Math.ceil(dailyLeadTarget / smsDailyCapacityPerNumber)) : 0
  const whatsappNumbersNeeded = hasWeeklyLeadsValue ? Math.max(1, Math.ceil(dailyLeadTarget / whatsappDailyCapacityPerNumber)) : 0

  const linkedinDailyInvitationsPerAccount = linkedinAccountsNeeded > 0 ? Math.ceil(weeklyLeadTarget / (linkedinAccountsNeeded * workingDaysPerWeek)) : 0
  const whatsappRequiredPortfolioTier = dailyLeadTarget > 0 ? ([250, 2000, 10000, 100000].find((tier) => tier >= dailyLeadTarget) ?? 'unlimited') : 0

  const resourceEstimates = selectedChannels.map((channel) => {
    switch (channel) {
      case 'Email':
        return {
          channel,
          type: 'email',
          primary: `${emailMailboxesNeeded} mailbox${emailMailboxesNeeded === 1 ? '' : 'es'}`,
          secondary: `${emailDomainsNeeded} domain${emailDomainsNeeded === 1 ? '' : 's'}`,
          detail: `${emailMonthlyVolume.toLocaleString()} emails/month at 2 emails per lead`,
          note: 'Each domain supports 5 mailboxes. Each mailbox is capped at 20 emails per working day.',
          internal: {
            monthlyEmailsNeeded: emailMonthlyVolume,
            mailboxMonthlyCapacity: emailMailboxMonthlyCapacity,
            mailboxesNeeded: emailMailboxesNeeded,
            domainsNeeded: emailDomainsNeeded,
          },
        }
      case 'LinkedIn':
        return {
          channel,
          type: 'linkedin',
          primary: `${linkedinAccountsNeeded} LinkedIn account${linkedinAccountsNeeded === 1 ? '' : 's'}`,
          secondary: `${linkedinWeeksSingleAccount} week${linkedinWeeksSingleAccount === 1 ? '' : 's'} with 1 account`,
          detail: `Safe planning is capped at 200 connection requests per account per week.`,
          note: 'Distribute invitations across working days, at random intervals, and share the 200/week budget across concurrent sequences. Profile retrieval also consumes account capacity.',
          internal: {
            accountsNeeded: linkedinAccountsNeeded,
            weeksWithSingleAccount: linkedinWeeksSingleAccount,
            weeklyCapacityPerAccount: linkedinWeeklyCapacityPerAccount,
            dailyInvitationsPerAccount: linkedinDailyInvitationsPerAccount,
            profileVisitsPerWorkingDay: { premium: 80, salesNavigator: 150 },
          },
        }
      case 'SMS':
        return {
          channel,
          type: 'sms',
          primary: `${smsNumbersNeeded} SMS number${smsNumbersNeeded === 1 ? '' : 's'}`,
          secondary: `${dailyLeadTarget.toLocaleString()} daily sends`,
          detail: 'Planning capacity: 2,000 outbound SMS per number per working day.',
          note: 'We size SMS around your working-day average volume.',
          internal: {
            numbersNeeded: smsNumbersNeeded,
            dailyLeadTarget,
            dailyCapacityPerNumber: smsDailyCapacityPerNumber,
          },
        }
      case 'WhatsApp':
        return {
          channel,
          type: 'whatsapp',
          primary: `${whatsappNumbersNeeded} WhatsApp number${whatsappNumbersNeeded === 1 ? '' : 's'}`,
          secondary: `${dailyLeadTarget.toLocaleString()} daily sends`,
          detail: 'Planning capacity: 1,000 outbound WhatsApp messages per number per working day.',
          note: 'Cloud API also requires sufficient business-portfolio messaging capacity for unique recipients outside the customer-service window. Adding numbers does not raise that shared limit. Cloud API tiers do not apply to session connections; their actual capacity depends on account behavior.',
          internal: {
            numbersNeeded: whatsappNumbersNeeded,
            dailyLeadTarget,
            dailyCapacityPerNumber: whatsappDailyCapacityPerNumber,
            requiredCloudApiPortfolioTier: whatsappRequiredPortfolioTier,
            uniqueRecipientsPerWorkingDay: dailyLeadTarget,
          },
        }
      case 'Phone':
        return {
          channel,
          type: 'phone',
          primary: 'Custom provisioning',
          secondary: `${dailyLeadTarget.toLocaleString()} daily call target`,
          detail: 'Phone capacity depends on routing, dialer setup, local numbers, and rep bandwidth.',
          note: 'We will scope numbers and call infrastructure manually after reviewing your target.',
          internal: {
            provisioning: 'custom',
            dailyLeadTarget,
          },
        }
      default:
        return null
    }
  }).filter(Boolean) as Array<{
    channel: string
    type: string
    primary: string
    secondary: string
    detail: string
    note: string
    internal: Record<string, unknown>
  }>

  return { weeklyLeadTarget, dailyLeadTarget, emailMonthlyVolume, emailMailboxesNeeded, emailDomainsNeeded, linkedinAccountsNeeded, smsNumbersNeeded, whatsappNumbersNeeded, linkedinWeeksSingleAccount, linkedinDailyInvitationsPerAccount, whatsappRequiredPortfolioTier, resourceEstimates };
}

/** Keep the calculator and Unlock Outreach request payloads in sync. */
export function buildOutreachRequestEstimates(weeklyLeads: number | string, selectedChannels: readonly string[], hasOutreachModule = false) {
  const { weeklyLeadTarget, dailyLeadTarget, resourceEstimates } = calculateOutreachResources(weeklyLeads, selectedChannels)
  return {
    requestMode: hasOutreachModule ? 'upgrade_resources' : 'unlock_outreach',
    weeklyLeadTarget,
    dailyLeadTarget,
    selectedChannels: [...selectedChannels],
    channels: Object.fromEntries(resourceEstimates.map((estimate) => [estimate.type, {
      label: estimate.channel, primary: estimate.primary, secondary: estimate.secondary,
      detail: estimate.detail, note: estimate.note, ...estimate.internal,
    }])),
  }
}
